import { useState, useEffect, useCallback } from 'react';
import { currencyService, CurrencyRates } from '../api/services/currencyService';

export interface UseCurrencyOptions {
  baseCurrency?: string;
  autoRefresh?: boolean;
}

export interface UseCurrencyReturn {
  // États
  rates: CurrencyRates | null;
  loading: boolean;
  error: string | null;
  
  // Fonctions
  convertCurrency: (amount: number, fromCurrency: string, toCurrency: string) => Promise<number>;
  formatCurrency: (amount: number, currency: string) => string;
  refreshRates: () => Promise<void>;
  clearCache: () => void;
  
  // Données
  supportedCurrencies: string[];
  baseCurrency: string;
}

export const useCurrency = (options: UseCurrencyOptions = {}): UseCurrencyReturn => {
  const { baseCurrency = 'EUR', autoRefresh = true } = options;
  
  const [rates, setRates] = useState<CurrencyRates | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Récupérer les taux de change
  const fetchRates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const exchangeRates = await currencyService.getExchangeRates(baseCurrency);
      setRates(exchangeRates);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la récupération des taux';
      setError(errorMessage);
      console.error('❌ Erreur dans useCurrency:', err);
    } finally {
      setLoading(false);
    }
  }, [baseCurrency]);

  // Charger les taux au montage et quand la devise de base change
  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  // Rafraîchir automatiquement toutes les 24h si activé
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      console.log('🔄 Rafraîchissement automatique des taux de change...');
      fetchRates();
    }, 24 * 60 * 60 * 1000); // 24 heures

    return () => clearInterval(interval);
  }, [fetchRates, autoRefresh]);

  // Convertir une devise
  const convertCurrency = useCallback(async (
    amount: number, 
    fromCurrency: string, 
    toCurrency: string
  ): Promise<number> => {
    try {
      return await currencyService.convertCurrency(amount, fromCurrency, toCurrency);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la conversion';
      setError(errorMessage);
      throw err;
    }
  }, []);

  // Formater une devise
  const formatCurrency = useCallback((amount: number, currency: string): string => {
    return currencyService.formatCurrency(amount, currency);
  }, []);

  // Rafraîchir manuellement les taux
  const refreshRates = useCallback(async () => {
    await fetchRates();
  }, [fetchRates]);

  // Vider le cache
  const clearCache = useCallback(() => {
    currencyService.clearCache();
    fetchRates(); // Recharger les taux
  }, [fetchRates]);

  return {
    // États
    rates,
    loading,
    error,
    
    // Fonctions
    convertCurrency,
    formatCurrency,
    refreshRates,
    clearCache,
    
    // Données
    supportedCurrencies: currencyService.getSupportedCurrencies(),
    baseCurrency
  };
}; 